/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Utilities for the Quick Schedule review step.
 *
 * @file quickScheduleReviewUtils.js
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import Utils from '../../common/utils';
import { parseDate, formatDateToStorage } from '../../common/dateTimeUtils';
import { PREFIX_ASSIGNMENT, SCHEDULE_MENU_HIDDEN_ITEM_IDS } from '../../common/constants';
import {
  findNodeById,
  collectAssignedContentNodeIds,
  calculateQuestionsForNode,
  getUnassignedNodeForSchedule,
  isChapterPartiallyAssigned,
  assignmentSlotHasContentUnderParent,
  chapterHasSectionsOnOtherSlots,
  collectLeafIdsUnderNode,
  findTopLevelChapterId
} from '../chapter-content/chapterContentTreeUtils';

const DAY_KEY_TO_INDEX = {
  Su: 0,
  M: 1,
  T: 2,
  W: 3,
  Th: 4,
  F: 5,
  S: 6
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function toLocalCalendarDate(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function formatDateIso(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export const ASSISTED_SCHEDULE_TREE_MENU_CONFIG = {
  items: [
    { id: SCHEDULE_MENU_HIDDEN_ITEM_IDS.ADD_TO_DATE },
    { id: SCHEDULE_MENU_HIDDEN_ITEM_IDS.PREVIEW, divider: true },
    { id: SCHEDULE_MENU_HIDDEN_ITEM_IDS.QUESTIONS }
  ],
  labels: {
    [SCHEDULE_MENU_HIDDEN_ITEM_IDS.ADD_TO_DATE]: 'Add to date',
    [SCHEDULE_MENU_HIDDEN_ITEM_IDS.PREVIEW]: 'Preview as student',
    [SCHEDULE_MENU_HIDDEN_ITEM_IDS.QUESTIONS]: 'Questions and topics'
  },
  hiddenAlways: [SCHEDULE_MENU_HIDDEN_ITEM_IDS.ADD_TO_DATE],
  hiddenWhenUnchecked: [],
  forScheduleToc: false
};

/** Add to date menu is enabled only for Quick Schedule step-2 TOC (scheduleMenuConfig.forScheduleToc). */
export function isScheduleTocAddToDateMenuEnabled(scheduleMenuConfig = {}) {
  return scheduleMenuConfig.forScheduleToc === true;
}

function parseScheduleCalendarDate(value) {
  if (!value) {
    return null;
  }

  const dateOnly = String(value).includes('T') ? String(value).split('T')[0] : value;

  return parseDate(dateOnly);
}

export const QUICK_SCHEDULE_ASSIGNMENT_LIST_ITEM_CONFIG = {
  showDragZone: true,
  showSections: true,
  showSectionRemoveButtons: true,
  inlineEditOnSelect: false,
  inlineTitleEdit: false,
  clickToEdit: false,
  showClearDataButton: true,
  showMenu: false,
  // Undated "Add new" slots show the Add date badge; the review step owns the date modal.
  showAddDatePlaceholder: true,
  scheduleBuilderLayout: true
};

/**
 * Calendar min/max for the Add to Date picker from step 1 schedule bounds.
 */
export function getScheduleDateBounds(scheduleFormValues = {}) {
  const start = parseScheduleCalendarDate(scheduleFormValues.startDate);
  const end = parseScheduleCalendarDate(scheduleFormValues.endDate);

  if (!start || !end) {
    return { minDate: null, maxDate: null };
  }

  return {
    minDate: toLocalCalendarDate(start),
    maxDate: toLocalCalendarDate(end)
  };
}

/**
 * Whether a YYYY-MM-DD value sits inside the step 1 start/end date range.
 * Add to Date allows any calendar day in that window, not only configured due weekdays.
 */
export function isDateWithinScheduleRange(dateIso, scheduleFormValues = {}) {
  const date = parseScheduleCalendarDate(dateIso);
  const start = parseScheduleCalendarDate(scheduleFormValues.startDate);
  const end = parseScheduleCalendarDate(scheduleFormValues.endDate);

  if (!date || !start || !end) {
    return false;
  }

  const dateMs = toLocalCalendarDate(date).getTime();
  const startMs = toLocalCalendarDate(start).getTime();
  const endMs = toLocalCalendarDate(end).getTime();

  return dateMs >= startMs && dateMs <= endMs;
}

function assignmentHasContent(assignment) {
  return (assignment?.contentNodes || []).length > 0;
}

/** Whether a schedule slot already has TOC content assigned. */
export function assignmentHasScheduleSlotContent(assignment) {
  return assignmentHasContent(assignment);
}

/**
 * Dates that already have content on an assignment card.
 * Quick Schedule allows the same calendar date on multiple slots; this helper is kept for
 * callers that need occupancy metadata without blocking date selection.
 */
export function getOccupiedScheduleDates(assignments = [], { excludeAssignmentId } = {}) {
  return new Set(
    assignments
      .filter((assignment) => (
        assignment.id !== excludeAssignmentId && assignmentHasContent(assignment)
      ))
      .map((assignment) => assignment.date)
      .filter(Boolean)
  );
}

/**
 * react-datepicker filterDate callback scoped to the step 1 date range.
 * Multiple Quick Schedule assignments may share the same due date.
 */
export function createScheduleAddToDateFilter(
  scheduleFormValues = {}
) {
  return (date) => {
    const dateIso = formatDateToStorage(date);

    return isDateWithinScheduleRange(dateIso, scheduleFormValues);
  };
}

/**
 * RHF validate rule for the Quick Schedule Add to Date modal.
 * Duplicate due dates across assignment slots are allowed.
 */
export function createScheduleAddToDateValidator(
  scheduleFormValues = {},
  { errorMessages = {} } = {}
) {
  const { required, outOfRange } = errorMessages;

  return (value) => {
    if (!value) {
      return required;
    }

    if (!isDateWithinScheduleRange(value, scheduleFormValues)) {
      return outOfRange;
    }

    return true;
  };
}

/**
 * Sort schedule slots by date. Slots from "Add new" / "Add to new assignment" are pinned:
 * they stay at the top in the order they were added, even after a date is picked.
 * Any other undated slot goes to the bottom.
 */
export function sortAssignmentsByDate(assignments = []) {
  return [...assignments].sort((left, right) => {
    if (left.isPinnedNewSlot || right.isPinnedNewSlot) {
      return (right.isPinnedNewSlot ? 1 : 0) - (left.isPinnedNewSlot ? 1 : 0);
    }

    if (!left.date || !right.date) {
      return (left.date ? 0 : 1) - (right.date ? 0 : 1);
    }

    return String(left.date).localeCompare(String(right.date));
  });
}

/** Whether any slot with content still has no due date (blocks Save and publish). */
export function hasUndatedAssignmentWithContent(assignments = []) {
  return (assignments || []).some((assignment) => (
    !assignment.date && assignmentHasContent(assignment)
  ));
}

function getNextAssignmentIndex(assignments = []) {
  return assignments.reduce((maxIndex, assignment) => {
    const match = String(assignment.id).match(/-qs-(\d+)/);

    if (!match) {
      return maxIndex;
    }

    return Math.max(maxIndex, Number.parseInt(match[1], 10));
  }, 0) + 1;
}

/** Locate the assignment card for a calendar date, if one already exists. */
export function findAssignmentByDate(assignments = [], dateIso) {
  if (!dateIso) {
    return null;
  }

  return assignments.find((assignment) => assignment.date === dateIso) || null;
}

/**
 * Build a new empty slot for a date outside the auto-generated weekday list.
 * Reuses step 1 due time and naming pattern from buildQuickScheduleAssignments.
 */
export function createAssignmentForDate({
  dateIso,
  scheduleFormValues = {},
  assignments = [],
  title
} = {}) {
  const nextIndex = getNextAssignmentIndex(assignments);
  const { dueTime = '' } = scheduleFormValues;

  return {
    id: `${PREFIX_ASSIGNMENT}-qs-${nextIndex}-${dateIso}`,
    title: title || `Assignment ${nextIndex}`,
    date: dateIso,
    time: dueTime,
    sections: [],
    chapters: [],
    contentNodes: []
  };
}

/**
 * Build an empty, undated slot for the step 2 "Add new" action.
 * The instructor picks the date later from the card's "Add date" badge;
 * `isPinnedNewSlot` keeps the card where it was added (see sortAssignmentsByDate).
 */
export function createUndatedAssignmentSlot({
  scheduleFormValues = {},
  assignments = []
} = {}) {
  const nextIndex = getNextAssignmentIndex(assignments);
  const { dueTime = '' } = scheduleFormValues;

  return {
    id: `${PREFIX_ASSIGNMENT}-qs-${nextIndex}-new`,
    title: `Assignment ${nextIndex}`,
    date: '',
    time: dueTime,
    sections: [],
    chapters: [],
    contentNodes: [],
    isPinnedNewSlot: true
  };
}

function upsertAssignmentByDate(assignments = [], assignment) {
  const existingIndex = assignments.findIndex((item) => item.date === assignment.date);

  if (existingIndex >= 0) {
    const nextAssignments = [...assignments];
    nextAssignments[existingIndex] = assignment;

    return nextAssignments;
  }

  return sortAssignmentsByDate([...assignments, assignment]);
}

const getChapterNumbersFromTitle = (title) => (
  Utils.getAssignmentChaptersNumbers([title]) || []
);

/**
 * Derive the label shown on assignment section chips from a content tree node title.
 * Numbered sections use the section number; unnumbered sections use the section title.
 */
export const getSectionChipFromNode = (node) => {
  if (!node?.title) {
    return null;
  }

  const sectionNumbers = getChapterNumbersFromTitle(node.title);
  const label = sectionNumbers.length
    ? sectionNumbers.join('.')
    : node.title.trim();

  if (!label) {
    return null;
  }

  return {
    id: node.id,
    label
  };
};

/** @deprecated Use getSectionChipFromNode */
export const getSectionNumbersFromNode = (node) => {
  const chip = getSectionChipFromNode(node);

  return chip ? [chip.label] : [];
};

/**
 * Expand a stored content node to draggable **leaf** nodes (sections).
 * Chapter/module nodes with children are walked recursively; leaves are returned as-is.
 *
 * @param {object|null} node
 * @returns {object[]}
 */
function collectLeafNodesFromContentNode(node) {
  if (!node) {
    return [];
  }

  const children = node.children || [];

  if (!children.length) {
    return [node];
  }

  // Recurse into children and flatten to a list of bottom-level section nodes.
  return children.flatMap(collectLeafNodesFromContentNode);
}

/**
 * Build a pruned TOC subtree that includes only leaves whose ids are in `allowedLeafIds`.
 * Parent chapter/module nodes are kept when they still have matching descendants.
 *
 * @param {object[]} nodes
 * @param {Set<string>} allowedLeafIds
 * @returns {object[]}
 */
function filterTreeToSectionIds(nodes = [], allowedLeafIds = new Set()) {
  return nodes.reduce((accumulator, node) => {
    const children = node.children || [];

    if (!children.length) {
      if (allowedLeafIds.has(node.id)) {
        accumulator.push(node);
      }

      return accumulator;
    }

    // Subtree under this parent that still matches allowed section ids.
    const filteredChildren = filterTreeToSectionIds(children, allowedLeafIds);

    if (!filteredChildren.length) {
      return accumulator;
    }

    accumulator.push({
      ...node,
      children: filteredChildren
    });

    return accumulator;
  }, []);
}

/**
 * Same-slot consolidation when a chapter is partially assigned schedule-wide but this card
 * already has section(s) under that parent and the user adds the parent (remaining sections).
 *
 * Replaces scattered section nodes with one filtered parent node so the UI shows a single
 * chapter chip (e.g. "1") on this slot only.
 *
 * @param {object[]} existingNodes — Current slot `contentNodes`
 * @param {object} unassignedSubtree — Payload being merged (often from getUnassignedNodeForSchedule)
 * @param {object} contextNode — Original TOC row (chapter/module) for parent id lookup
 * @param {object[]} sourceTocData
 * @returns {object[]} Next `contentNodes` for the slot (other chapters on the slot preserved)
 */
function consolidatePartialParentOnSlot(existingNodes = [], unassignedSubtree, contextNode, sourceTocData = []) {
  // Full chapter/module node from TOC (includes all children).
  const fullParent = findNodeById(sourceTocData, contextNode.id) || contextNode;
  // Every section id that belongs under this parent in the TOC.
  const parentLeafIds = new Set(collectLeafIdsUnderNode(fullParent));
  // Section ids that should appear on this slot after merge (existing + newly added).
  const sectionIdsOnSlot = new Set();

  existingNodes.forEach((node) => {
    if (node.id === fullParent.id || parentLeafIds.has(node.id)) {
      collectLeafNodesFromContentNode(node).forEach((leaf) => {
        if (leaf?.id) {
          sectionIdsOnSlot.add(leaf.id);
        }
      });
    }
  });

  collectLeafNodesFromContentNode(unassignedSubtree).forEach((leaf) => {
    if (leaf?.id) {
      sectionIdsOnSlot.add(leaf.id);
    }
  });

  // Content from other chapters on this slot — not part of the parent being consolidated.
  const keptNodes = existingNodes.filter((node) => (
    node.id !== fullParent.id && !parentLeafIds.has(node.id)
  ));
  // One chapter node containing only the section ids collected above.
  const filteredParent = filterTreeToSectionIds(
    [JSON.parse(JSON.stringify(fullParent))],
    sectionIdsOnSlot
  )[0];

  if (filteredParent) {
    keptNodes.push(filteredParent);
  }

  return keptNodes;
}

/**
 * Build `assignment.sections` chips from top-level `contentNodes`.
 * One chip per top-level node id (partial merges store leaf nodes at top level; full chapter stores one parent).
 *
 * @param {object[]} contentNodes
 * @returns {Array<{ id: string, label: string }>}
 */
export const getDisplaySectionsFromContentNodes = (contentNodes = []) => {
  // Chip ids already added — avoids duplicate chips for the same section.
  const seenIds = new Set();

  return (contentNodes || []).reduce((sections, node) => {
    const chip = getSectionChipFromNode(node);

    if (!chip || seenIds.has(chip.id)) {
      return sections;
    }

    seenIds.add(chip.id);
    sections.push(chip);

    return sections;
  }, []);
};

/**
 * Node tree used for merge: drag payloads may be leaves only; if the payload has no `children` array,
 * load the full chapter/module from `sourceTocData` for a standard full-chapter drop.
 *
 * @param {object} nodePayload — From drag JSON or Add to assignment selection
 * @param {object[]} sourceTocData
 * @returns {object}
 */
function resolveNodeForAssignmentMerge(nodePayload, sourceTocData = []) {
  if (!nodePayload?.id) {
    return nodePayload;
  }

  const payloadClone = JSON.parse(JSON.stringify(nodePayload));

  if (Array.isArray(payloadClone.children)) {
    return payloadClone;
  }

  // Look up the full TOC node when the drag payload is only a section stub.
  const tocNode = findNodeById(sourceTocData, nodePayload.id);

  return tocNode ? JSON.parse(JSON.stringify(tocNode)) : payloadClone;
}

/** Section drops use the parent chapter/module for split and consolidate checks. */
function resolveScheduleMergeContextNode(node, sourceTocData = []) {
  if (!node?.id) {
    return node;
  }

  const tocNode = findNodeById(sourceTocData, node.id) || node;

  if (tocNode.children?.length) {
    return tocNode;
  }

  const chapterId = findTopLevelChapterId(node.id, sourceTocData);

  if (!chapterId) {
    return tocNode;
  }

  return findNodeById(sourceTocData, chapterId) || tocNode;
}

/**
 * Whether the user acted on a chapter/module TOC row vs a single section row.
 *
 * Used to choose consolidate vs separate section chips when the slot already has
 * content from the same chapter. Parent menu / parent drag → consolidate when safe;
 * section drag → usually append leaves unless the chapter becomes complete on this slot.
 *
 * @param {object|null} mergeContextNode — Original menu row (parent when adding from parent menu); null on drag
 * @param {object} nodePayload — Node being merged (may be filtered to unassigned sections only)
 * @param {object[]} sourceTocData — Full TOC for parent vs leaf detection
 * @returns {boolean} True if the TOC row has children (chapter/module), false for a section leaf
 */
function isScheduleParentNodeDrop(mergeContextNode, nodePayload, sourceTocData = []) {
  // Menu path: mergeContextNode is the row they clicked. Drag path: use the dragged node.
  const tocRow = mergeContextNode || nodePayload;

  if (!tocRow?.id) {
    return false;
  }

  const tocNode = findNodeById(sourceTocData, tocRow.id) || tocRow;

  return Boolean(tocNode.children?.length);
}

/**
 * True when, after merging `nodePayload` into this slot, every section under the chapter
 * would be represented on this assignment card (and nowhere else on the schedule is
 * handled separately via `splitAcrossSlots` / `getUnassignedNodeForSchedule`).
 *
 * Example: slot already has 1.2; user adds section 1.1 and the chapter only has 1.1 + 1.2
 * → true → consolidate to one chip "1" even though the action was a section drop.
 *
 * @param {object[]} existingNodes — assignment.contentNodes before merge
 * @param {object} nodePayload — Incoming subtree (often unassigned leaves only)
 * @param {object} contextNode — Parent chapter from resolveScheduleMergeContextNode
 * @param {object[]} sourceTocData — Full TOC
 * @returns {boolean}
 */
function wouldCompleteChapterOnSlotAfterMerge(
  existingNodes,
  nodePayload,
  contextNode,
  sourceTocData = []
) {
  const fullParent = findNodeById(sourceTocData, contextNode.id) || contextNode;
  const allLeafIds = collectLeafIdsUnderNode(fullParent);

  if (!allLeafIds.length) {
    return false;
  }

  // Match stored contentNodes that belong to this chapter (parent node or section ids).
  const parentScopeIds = new Set(allLeafIds);

  parentScopeIds.add(fullParent.id);

  const onSlotAfter = new Set();

  existingNodes.forEach((node) => {
    if (node.id === fullParent.id || parentScopeIds.has(node.id)) {
      collectLeafNodesFromContentNode(node).forEach((leaf) => {
        if (leaf?.id) {
          onSlotAfter.add(leaf.id);
        }
      });
    }
  });

  collectLeafNodesFromContentNode(nodePayload).forEach((leaf) => {
    if (leaf?.id) {
      onSlotAfter.add(leaf.id);
    }
  });

  return allLeafIds.every((leafId) => onSlotAfter.has(leafId));
}

/**
 * Merge a TOC node into one assignment slot (core of step-2 drag-and-drop and Add to assignment).
 *
 * Branches (see `isChapterPartiallyAssigned` / `assignmentSlotHasContentUnderParent`):
 * 1. **Consolidate on slot** — parent drop, or section drop that completes the chapter on this slot only → one chapter `contentNode`
 * 2. **Section / partial** — append each unassigned **leaf** (including another section on the same slot)
 * 3. **Full chapter** — append one parent (or resolved TOC) node when not already on the slot
 *
 * Always refreshes `sections`, `chapters`, and `contentNodes` on the returned assignment copy.
 *
 * @param {object} assignment — Target schedule slot
 * @param {object} nodePayload — Filtered or full node to merge (often from getUnassignedNodeForSchedule)
 * @param {object[]} [sourceTocData] — TOC for partial/consolidate lookups
 * @param {object[]} [assignments] — All slots (for partial-chapter detection across the schedule)
 * @param {object|null} [mergeContextNode] — Original TOC row when payload is filtered (Add to assignment / preview)
 * @returns {object} Updated assignment
 */
export const applyDraggedNodeToAssignment = (
  assignment,
  nodePayload,
  sourceTocData = [],
  assignments = [],
  mergeContextNode = null
) => {
  // TOC row used for partial-chapter checks (menu row vs filtered payload).
  const contextNode = resolveScheduleMergeContextNode(
    mergeContextNode || nodePayload,
    sourceTocData
  );
  // What is already stored on this assignment card before the merge.
  const existingNodes = assignment.contentNodes || [];
  // Section ids already on this slot — skip adding the same leaf twice in partial mode.
  const seenIds = new Set(existingNodes.map((item) => item.id));
  // Working copy of contentNodes we will return.
  const nextContentNodes = [...existingNodes];
  // True when some sections of this chapter are on other dates but not all sections are assigned.
  const partialChapter = isChapterPartiallyAssigned(contextNode, sourceTocData, assignments);
  // Another assignment slot still holds a section from this chapter (e.g. 1.2 on Tue, adding to Mon).
  const splitAcrossSlots = chapterHasSectionsOnOtherSlots(
    contextNode,
    assignments,
    sourceTocData,
    assignment.id
  );
  // This card already has at least one section (or parent) from contextNode's chapter.
  const slotHasParentContent = assignmentSlotHasContentUnderParent(
    assignment,
    contextNode,
    sourceTocData
  );
  const isParentDrop = isScheduleParentNodeDrop(mergeContextNode, nodePayload, sourceTocData);
  const wouldCompleteChapterOnSlot = wouldCompleteChapterOnSlotAfterMerge(
    existingNodes,
    nodePayload,
    contextNode,
    sourceTocData
  );

  // Merge to a single chapter contentNode (one UI chip, e.g. "1"). Blocked when the same
  // chapter is split across multiple assignment slots — then we keep separate section chips.
  const consolidateSameParentOnSlot = slotHasParentContent
    && !splitAcrossSlots
    && (isParentDrop || wouldCompleteChapterOnSlot);

  // Add individual section leaves (1.1, 1.2, …) when the chapter is still partial on this
  // slot or schedule-wide, or when adding another section on the same slot without completing
  // the chapter. Mutually exclusive with consolidateSameParentOnSlot.
  const appendSectionLeaves = !consolidateSameParentOnSlot
    && (partialChapter || (!isParentDrop && slotHasParentContent && !splitAcrossSlots));

  if (consolidateSameParentOnSlot) {
    // Deep copy so merge helpers do not mutate the incoming payload.
    const unassignedSubtree = JSON.parse(JSON.stringify(nodePayload));

    nextContentNodes.length = 0;
    nextContentNodes.push(
      ...consolidatePartialParentOnSlot(
        existingNodes,
        unassignedSubtree,
        contextNode,
        sourceTocData
      )
    );
  } else if (appendSectionLeaves) {
    // Remaining unassigned sections only (not the full chapter tree on other slots).
    const unassignedSubtree = JSON.parse(JSON.stringify(nodePayload));

    collectLeafNodesFromContentNode(unassignedSubtree).forEach((leaf) => {
      if (!leaf?.id || seenIds.has(leaf.id)) {
        return;
      }

      seenIds.add(leaf.id);
      nextContentNodes.push(leaf);
    });
  } else {
    // Full chapter (or resolved TOC tree) for a normal parent drop.
    const node = resolveNodeForAssignmentMerge(nodePayload, sourceTocData);
    // Full-chapter path: do not add the same top-level node id twice.
    const alreadyAdded = existingNodes.some((item) => item.id === node.id);

    if (!alreadyAdded) {
      nextContentNodes.push(node);
    }
  }

  // Rebuild UI chips and chapter list from the merged contentNodes.
  const nextSections = getDisplaySectionsFromContentNodes(nextContentNodes);
  const nextChapters = nextContentNodes.filter((item) => item.type === 'chapter' || item.type === 'module');

  return {
    ...assignment,
    sections: nextSections,
    chapters: nextChapters,
    contentNodes: nextContentNodes
  };
};

/**
 * Preview merge for Add to assignment modal (does not mutate the assignments array).
 * Strips already-assigned sections via `getUnassignedNodeForSchedule`, then runs `applyDraggedNodeToAssignment`.
 *
 * @param {object} params
 * @param {object} params.assignment — Slot row being previewed
 * @param {object} params.nodePayload — TOC node from the menu
 * @param {object[]} [params.sourceTocData]
 * @param {object[]} [params.assignments] — Full draft schedule (for partial detection)
 * @returns {object} Assignment copy with preview content, or unchanged if nothing left to assign
 */
export function previewAddNodeOnAssignment({
  assignment,
  nodePayload,
  sourceTocData = [],
  assignments = []
}) {
  if (!assignment || !nodePayload?.id) {
    return assignment;
  }

  // Sections still available to assign from this TOC row (others may already be on the schedule).
  const unassignedNode = getUnassignedNodeForSchedule(
    nodePayload,
    sourceTocData,
    assignments
  );

  if (!unassignedNode) {
    return assignment;
  }

  // Preview uses filtered subtree for merge but keeps original menu row as merge context.
  return applyDraggedNodeToAssignment(
    assignment,
    unassignedNode,
    sourceTocData,
    assignments,
    nodePayload
  );
}

/**
 * Persist Add to assignment **Done**: merge TOC node onto an existing slot by id (not by calendar date).
 * Same rules as `applyContentDrop` / drag-and-drop. Host app (one-reader) calls this from the modal Done handler.
 *
 * @param {object} params
 * @param {object[]} [params.assignments] — Current step-2 slot list
 * @param {string} params.assignmentId — Selected slot in the modal
 * @param {object} params.nodePayload — TOC node from Add to assignment
 * @param {object[]} [params.sourceTocData] — Restricted TOC used in review step
 * @returns {{ assignments: object[], assignedNodeIds: Set, targetAssignmentId: string }}
 */
export function applyAddNodeToAssignmentSlot({
  assignments = [],
  assignmentId,
  nodePayload,
  sourceTocData = []
}) {
  if (!nodePayload?.id || !assignmentId) {
    return {
      assignments,
      assignedNodeIds: collectAssignedContentNodeIds(assignments),
      targetAssignmentId: assignmentId
    };
  }

  // The schedule card the user picked in the Add to assignment modal.
  const selectedAssignment = assignments.find((item) => item.id === assignmentId);

  if (!selectedAssignment) {
    return {
      assignments,
      assignedNodeIds: collectAssignedContentNodeIds(assignments),
      targetAssignmentId: assignmentId
    };
  }

  // TOC subtree with sections already assigned elsewhere removed.
  const unassignedNode = getUnassignedNodeForSchedule(
    nodePayload,
    sourceTocData,
    assignments
  );

  if (!unassignedNode) {
    return {
      assignments,
      assignedNodeIds: collectAssignedContentNodeIds(assignments),
      targetAssignmentId: assignmentId
    };
  }

  // Merged target slot (same rules as drag-and-drop on step 2).
  const updatedAssignment = applyDraggedNodeToAssignment(
    selectedAssignment,
    unassignedNode,
    sourceTocData,
    assignments,
    nodePayload
  );
  // Full slot list with only the target card replaced by the merged result.
  const nextAssignments = assignments.map((assignment) => (
    assignment.id === assignmentId ? updatedAssignment : assignment
  ));

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments),
    targetAssignmentId: assignmentId
  };
}

/**
 * Copy populated content from one assignment card onto another date.
 * Original card is left unchanged; content is merged into the target date card.
 */
export function applyAddAssignmentContentToDate({
  assignments = [],
  sourceAssignmentId,
  targetDateIso,
  sourceTocData = [],
  scheduleFormValues = {}
}) {
  const sourceAssignment = assignments.find((item) => item.id === sourceAssignmentId);

  if (!sourceAssignment || !targetDateIso) {
    return { assignments, assignedNodeIds: collectAssignedContentNodeIds(assignments) };
  }

  if (sourceAssignment.date === targetDateIso) {
    return { assignments, assignedNodeIds: collectAssignedContentNodeIds(assignments) };
  }

  let targetAssignment = findAssignmentByDate(assignments, targetDateIso);

  if (!targetAssignment) {
    targetAssignment = createAssignmentForDate({
      dateIso: targetDateIso,
      scheduleFormValues,
      assignments,
      title: sourceAssignment.title
    });
  }

  const contentNodes = sourceAssignment.contentNodes || [];
  let mergedAssignment = { ...targetAssignment };

  contentNodes.forEach((node) => {
    mergedAssignment = applyDraggedNodeToAssignment(mergedAssignment, node, sourceTocData, assignments);
  });

  const nextAssignments = upsertAssignmentByDate(assignments, mergedAssignment);

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments),
    targetAssignmentId: mergedAssignment.id
  };
}

/** Persist an inline title edit from the schedule builder card. */
export function applyAssignmentTitleUpdate({
  assignments = [],
  assignmentId,
  title
} = {}) {
  const trimmedTitle = String(title || '').trim();

  const nextAssignments = assignments.map((assignment) => {
    if (assignment.id !== assignmentId) {
      return assignment;
    }

    return {
      ...assignment,
      title: trimmedTitle || assignment.title
    };
  });

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments)
  };
}

/** Total question count across an assignment's assigned content nodes. */
export function getQuickScheduleSlotQuestionCount(
  assignment,
  { questionCounts = [], sourceTocData = [] } = {}
) {
  const contentNodes = assignment?.contentNodes || [];

  return contentNodes.reduce(
    (sum, node) => sum + calculateQuestionsForNode(node, questionCounts, sourceTocData),
    0
  );
}

/**
 * Checks whether a populated slot needs the inline "can't be published" warning
 */
export function hasQuickScheduleSlotZeroQuestions(assignment, options = {}) {
  if (!assignmentHasContent(assignment)) {
    return false;
  }

  return getQuickScheduleSlotQuestionCount(assignment, options) === 0;
}

/**
 * Whether at least one schedule slot has content with 1+ questions.
 * Save/Finish enables as soon as this is true
 */
export function hasAnyQuickScheduleSlotWithQuestions(assignments = [], options = {}) {
  if (!Array.isArray(assignments) || !assignments.length) {
    return false;
  }

  return assignments.some((assignment) => (
    assignmentHasContent(assignment) && getQuickScheduleSlotQuestionCount(assignment, options) > 0
  ));
}

/**
 * Generate empty assignment slots for each configured due weekday between start and end.
 */
export const buildQuickScheduleAssignments = (formValues = {}) => {
  const {
    dueDays = [],
    dueTime = '',
    startDate = '',
    endDate = ''
  } = formValues;

  if (!dueDays.length || !startDate || !endDate) {
    return [];
  }

  const start = parseScheduleCalendarDate(startDate);
  const end = parseScheduleCalendarDate(endDate);

  if (!start || !end) {
    return [];
  }

  const dueDayIndexes = new Set(
    dueDays.map((dayKey) => DAY_KEY_TO_INDEX[dayKey]).filter((value) => value !== undefined)
  );

  const assignments = [];
  let assignmentIndex = 1;
  const startMs = toLocalCalendarDate(start).getTime();
  const endMs = toLocalCalendarDate(end).getTime();

  for (let cursorMs = startMs; cursorMs <= endMs; cursorMs += MS_PER_DAY) {
    const cursorDate = new Date(cursorMs);

    if (dueDayIndexes.has(cursorDate.getDay())) {
      assignments.push({
        id: `${PREFIX_ASSIGNMENT}-qs-${assignmentIndex}`,
        title: `Assignment ${assignmentIndex}`,
        date: formatDateIso(cursorDate),
        time: dueTime,
        sections: [],
        chapters: [],
        contentNodes: []
      });
      assignmentIndex += 1;
    }
  }

  return assignments;
};

export const removeSectionFromAssignment = (assignment, sectionKey) => {
  const normalizedKey = String(sectionKey);

  const nextContentNodes = (assignment.contentNodes || []).filter((node) => {
    if (node.id === sectionKey) {
      return false;
    }

    const chip = getSectionChipFromNode(node);

    return chip?.label !== normalizedKey && chip?.id !== sectionKey;
  });
  const nextSections = getDisplaySectionsFromContentNodes(nextContentNodes);
  const nextChapters = nextContentNodes.filter(
    (item) => item.type === 'chapter' || item.type === 'module'
  );

  return {
    ...assignment,
    sections: nextSections,
    chapters: nextChapters,
    contentNodes: nextContentNodes
  };
};

export const clearAssignmentContent = (assignment) => ({
  ...assignment,
  sections: [],
  chapters: [],
  contentNodes: []
});

/**
 * Step-2 drag-and-drop handler: merge `nodePayload` onto the slot matching `assignmentId`.
 * Same pre-filter as Add to assignment (`getUnassignedNodeForSchedule`) so partially assigned
 * parents only add remaining sections on the right panel.
 */
export const applyContentDrop = ({
  assignments = [],
  assignmentId,
  nodePayload,
  sourceTocData = []
}) => {
  if (!nodePayload?.id || !assignmentId) {
    return {
      assignments,
      assignedNodeIds: collectAssignedContentNodeIds(assignments)
    };
  }

  const unassignedNode = getUnassignedNodeForSchedule(
    nodePayload,
    sourceTocData,
    assignments
  );

  if (!unassignedNode) {
    return {
      assignments,
      assignedNodeIds: collectAssignedContentNodeIds(assignments)
    };
  }

  // Update only the drop target card; leave all other dates unchanged.
  const nextAssignments = assignments.map((assignment) => {
    if (assignment.id !== assignmentId) {
      return assignment;
    }

    return applyDraggedNodeToAssignment(
      assignment,
      unassignedNode,
      sourceTocData,
      assignments,
      nodePayload
    );
  });

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments)
  };
};

/** Remove one section chip and its backing content node from an assignment. */
export const applySectionRemove = ({
  assignments = [],
  assignmentId,
  sectionNumber
}) => {
  const nextAssignments = assignments.map((assignment) => {
    if (assignment.id !== assignmentId) {
      return assignment;
    }

    return removeSectionFromAssignment(assignment, sectionNumber);
  });

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments)
  };
};

/** Clear all content from an assignment while keeping the scheduled date slot. */
export const applyClearAssignmentContent = ({
  assignments = [],
  assignmentId
}) => {
  const nextAssignments = assignments.map((assignment) => (
    assignment.id === assignmentId ? clearAssignmentContent(assignment) : assignment
  ));

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments)
  };
};

/**
 * Move a schedule card to another date within the step 1 range.
 * Multiple assignment slots may use the same calendar date (no swap or merge with siblings).
 */
export function applyRescheduleAssignmentDate({
  assignments = [],
  assignmentId,
  targetDateIso
}) {
  if (!assignmentId || !targetDateIso) {
    return { assignments, assignedNodeIds: collectAssignedContentNodeIds(assignments) };
  }

  const sourceAssignment = assignments.find((item) => item.id === assignmentId);

  if (!sourceAssignment) {
    return { assignments, assignedNodeIds: collectAssignedContentNodeIds(assignments) };
  }

  if (sourceAssignment.date === targetDateIso) {
    return {
      assignments,
      assignedNodeIds: collectAssignedContentNodeIds(assignments),
      targetAssignmentId: sourceAssignment.id
    };
  }

  const nextAssignments = assignments.map((assignment) => (
    assignment.id === assignmentId
      ? { ...assignment, date: targetDateIso }
      : assignment
  ));

  const sortedAssignments = sortAssignmentsByDate(nextAssignments);

  return {
    assignments: sortedAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(sortedAssignments),
    targetAssignmentId: assignmentId
  };
}

/**
 * Insert an undated "Add new" slot at the top of the schedule (newest first).
 * With `nodePayload` (Add to assignment → Add to new assignment) the TOC node is merged
 * onto the new slot using the same rules as drag-and-drop.
 */
export function applyAddNewAssignmentSlot({
  assignments = [],
  scheduleFormValues = {},
  nodePayload = null,
  sourceTocData = []
} = {}) {
  const newSlot = createUndatedAssignmentSlot({ scheduleFormValues, assignments });
  const nextAssignments = [newSlot, ...assignments];

  if (nodePayload?.id) {
    return applyAddNodeToAssignmentSlot({
      assignments: nextAssignments,
      assignmentId: newSlot.id,
      nodePayload,
      sourceTocData
    });
  }

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments),
    targetAssignmentId: newSlot.id
  };
}

/** Remove an empty schedule slot card from the builder list. */
export function applyRemoveAssignmentSlot({
  assignments = [],
  assignmentId
} = {}) {
  const nextAssignments = assignments.filter((assignment) => assignment.id !== assignmentId);

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments)
  };
}
